<template>
  <v-card :class="['danger-card', { 'danger-card--archived': danger.archived }]" variant="outlined">
    <v-card-item>
      <template #prepend>
        <v-icon :color="danger.archived ? 'grey' : 'error'" size="large">
          {{ danger.archived ? "mdi-archive-outline" : "mdi-alert" }}
        </v-icon>
      </template>
      <v-card-title class="text-wrap">{{ danger.rapid_name || "Rapide non précisé" }}</v-card-title>
      <v-card-subtitle class="text-wrap">{{ typeLabel }}</v-card-subtitle>
      <template #append>
        <v-chip v-if="danger.archived" size="small" variant="tonal">Archivé</v-chip>
        <v-chip
          v-else-if="toReconfirm"
          size="small"
          color="warning"
          variant="flat"
          prepend-icon="mdi-clock-alert-outline"
          :title="`Aucune vérification depuis plus de ${reconfirmDays} jours`"
        >
          À reconfirmer
        </v-chip>
      </template>
    </v-card-item>

    <v-card-text>
      <div v-if="impactLabels.length" class="d-flex flex-wrap ga-2 mb-3">
        <v-chip
          v-for="impact in impactLabels"
          :key="impact"
          size="small"
          :color="danger.archived ? undefined : 'error'"
          variant="tonal"
        >
          {{ impact }}
        </v-chip>
      </div>
      <p v-if="canStopLabel" class="mb-2"><strong>Peut-on s'arrêter avant le danger :</strong> {{ canStopLabel }}</p>
      <p v-if="danger.details" class="danger-details mb-3">{{ danger.details }}</p>

      <div v-if="images.length" class="d-flex flex-wrap ga-2 mb-3">
        <button
          v-for="(image, index) in images"
          :key="image.path || image.url"
          type="button"
          class="photo-thumb"
          :title="`Agrandir la photo ${index + 1}`"
          @click="openPhoto(index)"
        >
          <img :src="image.url" :alt="`Photo du danger ${index + 1}`" loading="lazy" />
        </button>
      </div>

      <div class="text-body-small text-medium-emphasis">
        <div>{{ startText }}</div>
        <div v-if="danger.last_checkup">
          {{ lastCheckupText }}
          <a v-if="checkups.length" href="#" class="ml-1 text-primary" @click.prevent="historyOpen = true">
            Historique ({{ checkups.length }})
          </a>
        </div>
        <div v-else>Aucune vérification depuis le signalement</div>
        <div v-if="danger.archived && danger.archived_by">
          Archivé par {{ danger.archived_by.displayName || "un membre" }}
        </div>
      </div>
    </v-card-text>

    <v-card-actions v-if="hasActions" class="flex-wrap">
      <v-btn
        v-if="canCheckup && !danger.archived"
        color="primary"
        variant="tonal"
        prepend-icon="mdi-check-decagram"
        @click="$emit('checkup', danger)"
      >
        Vérifier
      </v-btn>
      <v-btn
        v-if="canCheckup && danger.archived"
        variant="text"
        prepend-icon="mdi-archive-arrow-up-outline"
        @click="$emit('unarchive', danger)"
      >
        Réactiver
      </v-btn>
      <v-btn v-if="canEdit" variant="text" prepend-icon="mdi-pencil" @click="$emit('edit', danger)">Modifier</v-btn>
      <v-spacer></v-spacer>
      <v-btn v-if="canFlag" variant="text" size="small" prepend-icon="mdi-flag-outline" @click="$emit('flag', danger)">
        Signaler aux administrateurs
      </v-btn>
    </v-card-actions>

    <v-dialog v-model="historyOpen" max-width="560" scrollable>
      <v-card title="Historique des vérifications">
        <v-card-text>
          <v-timeline density="compact" side="end">
            <v-timeline-item
              v-for="checkup in checkupsNewestFirst"
              :key="checkup.at"
              dot-color="primary"
              size="x-small"
            >
              <div class="font-weight-medium">{{ formatDangerDate(checkup.date) }}</div>
              <div class="text-body-small text-medium-emphasis">
                par {{ (checkup.by && checkup.by.displayName) || "un membre" }}
              </div>
              <div v-if="checkup.comment" class="text-body-medium">{{ checkup.comment }}</div>
            </v-timeline-item>
          </v-timeline>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="historyOpen = false">Fermer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="photoOpen" fullscreen>
      <v-card>
        <v-toolbar density="compact">
          <v-toolbar-title class="text-body-large">{{ danger.rapid_name || typeLabel }}</v-toolbar-title>
          <v-btn icon="mdi-close" title="Fermer" @click="photoOpen = false"></v-btn>
        </v-toolbar>
        <div class="photo-viewer">
          <zoomable-image
            v-if="photoOpen"
            :src="images[photoIndex].url"
            :has-previous="photoIndex > 0"
            :has-next="photoIndex < images.length - 1"
            @previous="photoIndex--"
            @next="photoIndex++"
          ></zoomable-image>
        </div>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
  import ZoomableImage from "@/components/ZoomableImage.vue";
  import {
    DANGER_RECONFIRM_DAYS,
    dangerCanStopLabel,
    dangerImpactLabels,
    dangerTypeLabel,
    formatDangerDate,
    needsReconfirmation,
  } from "@/utils/dangers";

  export default {
    components: { ZoomableImage },
    props: {
      danger: { type: Object, required: true },
      // Active member or admin: may add a check-up, archive and reactivate.
      canCheckup: Boolean,
      // Author or admin
      canEdit: Boolean,
      // Signed in
      canFlag: Boolean,
    },
    emits: ["checkup", "unarchive", "edit", "flag"],
    data() {
      return {
        reconfirmDays: DANGER_RECONFIRM_DAYS,
        historyOpen: false,
        photoOpen: false,
        photoIndex: 0,
      };
    },
    computed: {
      typeLabel() {
        return dangerTypeLabel(this.danger);
      },
      impactLabels() {
        return dangerImpactLabels(this.danger);
      },
      canStopLabel() {
        return dangerCanStopLabel(this.danger);
      },
      toReconfirm() {
        return needsReconfirmation(this.danger);
      },
      images() {
        return Array.isArray(this.danger.images) ? this.danger.images : [];
      },
      checkups() {
        return Array.isArray(this.danger.checkups) ? this.danger.checkups : [];
      },
      checkupsNewestFirst() {
        return [...this.checkups].reverse();
      },
      startText() {
        const author = this.danger.created_by && this.danger.created_by.displayName;
        const since = `Depuis le ${formatDangerDate(this.danger.start_date)}`;
        return author ? `${since}, signalé par ${author}` : since;
      },
      lastCheckupText() {
        const checkup = this.danger.last_checkup;
        if (!checkup) return "";
        let text = `Dernière vérification : ${formatDangerDate(checkup.date)}`;
        if (checkup.by && checkup.by.displayName) text += ` par ${checkup.by.displayName}`;
        if (checkup.comment) text += ` : « ${checkup.comment} »`;
        return text;
      },
      hasActions() {
        return this.canCheckup || this.canEdit || this.canFlag;
      },
    },
    methods: {
      formatDangerDate,
      openPhoto(index) {
        this.photoIndex = index;
        this.photoOpen = true;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .danger-card {
    border-left: 4px solid rgb(var(--v-theme-error));
  }
  .danger-card--archived {
    border-left-color: rgb(var(--v-theme-surface-variant));
  }
  .danger-details {
    white-space: pre-line;
  }
  .photo-thumb {
    width: 112px;
    aspect-ratio: 4 / 3;
    border-radius: 6px;
    overflow: hidden;
    padding: 0;
    border: 0;
    cursor: zoom-in;
    background: rgb(var(--v-theme-surface-variant));

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
  }
  .photo-viewer {
    height: calc(100vh - 48px);
  }
</style>
